import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import {
  downloadBlob,
  downloadScaleLoad,
  getAllPdvs,
  getScaleLoadProducts,
  solicitarCargaBalancaPdv,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function ScaleLoadPage() {
  const [items, setItems] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [pdvId, setPdvId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prodRes, pdvRes] = await Promise.all([
        getScaleLoadProducts(),
        getAllPdvs(),
      ]);
      setItems(prodRes.data || []);
      const lista = Array.isArray(pdvRes.data) ? pdvRes.data : [];
      setPdvs(lista.filter((p) => p.cargaBalanca && (p.balancaPasta || '').trim()));
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível listar os produtos de carga.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (marca, filename) => {
    try {
      const response = await downloadScaleLoad(marca);
      downloadBlob(response.data, filename);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao gerar a carga de balança.'));
    }
  };

  const enviarPdv = async () => {
    setError('');
    setOkMsg('');
    if (!pdvId) {
      setError('Selecione um PDV com pasta de carga configurada.');
      return;
    }
    try {
      const { data } = await solicitarCargaBalancaPdv(Number(pdvId));
      setOkMsg(
        `Carga solicitada para o PDV. Pasta: ${data?.pasta || '—'}. ` +
          'Na próxima sincronização ele gravará TXITENS/CADTXT para o MGV.'
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao solicitar carga no PDV.'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Carga de balança"
        subtitle={
          <>
            Produtos com balança = Carga (2). Download manual ou envio para um PDV
            (ele grava na pasta configurada — MGV em carga automática/remota).
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!items.length}
            onClick={() => download('todas', 'carga-balanca.zip')}
          >
            Baixar Toledo + Filizola
          </Button>
          <Button
            variant="outlined"
            disabled={!items.length}
            onClick={() => download('toledo', 'TXITENS.txt')}
          >
            Só Toledo
          </Button>
          <Button
            variant="outlined"
            disabled={!items.length}
            onClick={() => download('filizola', 'CADTXT.txt')}
          >
            Só Filizola
          </Button>
          <Button onClick={load} disabled={loading}>Atualizar</Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems={{ md: 'center' }}>
          <TextField
            select
            size="small"
            label="Enviar para PDV"
            value={pdvId}
            onChange={(e) => setPdvId(e.target.value)}
            sx={{ minWidth: 320 }}
            helperText={pdvs.length ? 'Só PDVs com carga + pasta' : 'Nenhum PDV com pasta configurada'}
          >
            {pdvs.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.descricao || `PDV #${p.id}`} — {p.balancaPasta}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            disabled={!pdvs.length || !pdvId}
            onClick={enviarPdv}
          >
            Solicitar carga no PDV
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {!loading && !items.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum produto marcado para carga de balança. No cadastro, use Balança = 2 (Carga).
        </Alert>
      )}

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Unidade</TableCell>
              <TableCell align="right">Validade (dias)</TableCell>
              <TableCell align="right">Preço varejo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((x) => (
              <TableRow key={x.id}>
                <TableCell data-label="Código">{x.codigo}</TableCell>
                <TableCell data-label="Descrição">{x.descricao}</TableCell>
                <TableCell data-label="Unidade">{x.unidade}</TableCell>
                <TableCell align="right" data-label="Validade (dias)">{x.validade}</TableCell>
                <TableCell align="right" data-label="Preço varejo">{currency(x.precoVarejo)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
    </Box>
  );
}
